/**
 * Scenario Nudges Component
 * Shows interactive tooltips/guidance on first visit to a scenario
 */

import React, { useState, useEffect } from 'react';
import './ScenarioNudges.css';
import { X, Info, MessageCircle, FileText, Target } from 'lucide-react';

const ScenarioNudges = ({ scenarioId, onComplete }) => {
  const [currentNudge, setCurrentNudge] = useState(0);
  const [showNudges, setShowNudges] = useState(false);

  useEffect(() => {
    // Check if nudges have been shown for this scenario
    const nudgesShown = localStorage.getItem(`nudges-shown-${scenarioId}`);
    if (!nudgesShown) {
      setShowNudges(true);
    }
  }, [scenarioId]);

  const nudges = [
    {
      id: 'context',
      title: 'Read the Context',
      message: 'Start by reading the context panel above. It contains important information about the company, situation, and key stakeholders.',
      icon: Info,
      target: 'context-panel'
    },
    {
      id: 'tasks',
      title: 'Review Your Tasks',
      message: 'Check the tasks panel on the left. These are the objectives you need to complete for this scenario.',
      icon: Target,
      target: 'tasks-panel'
    },
    {
      id: 'chat',
      title: 'Chat with Stakeholders',
      message: 'Use the chat interface to talk to stakeholders. They have the information you need to complete your tasks.',
      icon: MessageCircle,
      target: 'chat-interface'
    },
    {
      id: 'artifacts',
      title: 'Upload Artifacts',
      message: 'Click the Workspace button to upload documents, PRDs, or other artifacts you create while working on tasks.',
      icon: FileText,
      target: 'workspace-button'
    }
  ];

  const handleNext = () => {
    if (currentNudge < nudges.length - 1) {
      setCurrentNudge(currentNudge + 1);
    } else {
      handleComplete();
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(`nudges-shown-${scenarioId}`, 'true');
    setShowNudges(false);
    if (onComplete) {
      onComplete();
    }
  };

  if (!showNudges || currentNudge >= nudges.length) {
    return null;
  }

  const nudge = nudges[currentNudge];
  const Icon = nudge.icon;

  return (
    <div className="scenario-nudges-overlay">
      <div className="scenario-nudge-card">
        <div className="nudge-header">
          <div className="nudge-icon">
            <Icon size={20} />
          </div>
          <h3>{nudge.title}</h3>
          <button className="nudge-close" onClick={handleSkip}>
            <X size={18} />
          </button>
        </div>
        <div className="nudge-content">
          <p>{nudge.message}</p>
        </div>
        <div className="nudge-footer">
          <div className="nudge-progress">
            {nudges.map((_, index) => (
              <div
                key={index}
                className={`nudge-dot ${index === currentNudge ? 'active' : ''} ${index < currentNudge ? 'completed' : ''}`}
              />
            ))}
          </div>
          <div className="nudge-actions">
            {currentNudge < nudges.length - 1 ? (
              <button className="nudge-button secondary" onClick={handleSkip}>
                Skip
              </button>
            ) : null}
            <button className="nudge-button primary" onClick={handleNext}>
              {currentNudge === nudges.length - 1 ? 'Got it!' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenarioNudges;

